import { ALL_TRAFFIC } from '@utils/filter';
import calcShortcutInterval from '@utils/calcShortcutInterval';
import { DATE_SHORTCUTS } from '@utils/constants';
import getGranularity from '@utils/granularity';
import { FLOW_PRESET_DEFAULT_DIMENSIONS } from '@src/utils/dimension/appDimensionHelper';
import { type AppFlowMetadata } from '@src/hooks/flow/useFlowConfigs';
import { SUMMARY_PANEL_TYPE } from '@src/components/top-widget-panel/constants';

const calIntervalObjFromShortcut = (shortcut) => {
  const [from, to] = calcShortcutInterval(shortcut);
  return {
    shortcut,
    from,
    to,
    granularity: getGranularity({ from, to, minuteGranularity: true, overview: false, shortcut }),
  };
};

export const createFlowPresets = ({ metrics, validateDimension, flowConfigMeta }) => {
  const flowUseCases = Object.keys(flowConfigMeta as Record<string, AppFlowMetadata>).reduce(
    (flows, flowKey) => {
      const flowConfig = flowConfigMeta[flowKey];
      // Only select these 4 specific metrics: init, success %, avg_complete_duration, flows_with_errors
      const selectedMetricKeys = ['init', 'success', 'avg_complete_duration', 'flows_with_errors'];
      const selectedMetricIds = selectedMetricKeys
        .map((key) => flowConfig.metrics[key])
        .filter((id) => id && metrics[id]?.active);

      const flowMetrics = flowConfig.metaDefinition.metrics
        .filter(
          (it) =>
            !!it.type &&
            metrics[it.identifier]?.active &&
            it.scope !== 'service' &&
            selectedMetricIds.includes(it.identifier),
        )
        .sort((a, b) => (a.output_order ?? Infinity) - (b.output_order ?? Infinity))
        .map((it) => it.identifier);

      if (!flowMetrics.length) return flows;
      const presetConfig = {
        title: flowConfig.name,
        key: flowKey,
        config: {
          metricsChartContainer: {
            metric: flowMetrics[0],
            metrics: Object.assign(flowMetrics),
          },
          common: {
            appliedFilters: [ALL_TRAFFIC],
            interval: calIntervalObjFromShortcut(DATE_SHORTCUTS.last6Hours),
          },
          'module:trends': {
            base: {
              dimensions: validateDimension(FLOW_PRESET_DEFAULT_DIMENSIONS),
              dimensionMetrics: flowMetrics,
            },
          },
          toggles: {
            sections: {
              summary: true,
            },
            diagnostics: {
              aiAlert: false,
              manualAlert: false,
            },
          },
          summaryWidgets: {
            type: SUMMARY_PANEL_TYPE.FLOW,
            widgets: [],
          },
        },
      };

      return {
        ...flows,
        [flowKey]: presetConfig,
      };
    },
    {} as Record<string, unknown>,
  );

  return flowUseCases;
};
